import React, { FC } from 'react';
import { Dialog, DialogContent, DialogTitle } from '../../../ui/components/Dialog';
import type { CardOffer } from '../types/handTypes';
import OfferChooser, { offerReason } from './OfferChooser';
import { BRICK, DECK_INK, DECK_PAPER, Note as Pen, SERIF, useSize } from './deck';
import type { HandEntry } from './handModel';

interface NewCardModalProps {
  open: boolean;
  onClose: () => void;
  offer: CardOffer;
  held: HandEntry[];
  full: boolean;
  busy: boolean;
  /** when the new card can be played, in words */
  whenLine: string;
  /** the last keep or throw failed */
  error: string | null;
  onKeep: (discardInstanceId?: string) => void;
  onThrow: () => void;
}

/**
 * "you got a new card!": the hand opens it when you arrive on the AOTD page
 * with an offer waiting (once per offer per visit). The card in full, where it
 * came from and when it can be played, then keep or throw it, or with a full
 * deck pick the card to swap out. Closing it leaves the offer in the hand.
 */
const NewCardModal: FC<NewCardModalProps> = ({ open, onClose, offer, held, full, busy, whenLine, error, onKeep, onThrow }) => {
  const [bodyRef, body] = useSize<HTMLDivElement>();
  const reason = offerReason(offer);
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        className="max-w-md"
        aria-describedby={undefined}
        style={{ background: DECK_PAPER, border: `1px solid ${DECK_INK}`, borderRadius: 9 }}
      >
        <div ref={bodyRef}>
          <DialogTitle
            style={{
              fontFamily: SERIF,
              fontStyle: 'italic',
              fontWeight: 600,
              fontSize: 24,
              lineHeight: 1.15,
              letterSpacing: 0,
              color: DECK_INK,
              margin: '0 36px 2px 0',
            }}
          >
            you got a new card!
          </DialogTitle>
          {reason && <Pen size={15}>{reason}</Pen>}
          <div style={{ marginTop: 14 }}>
            <OfferChooser
              offer={offer}
              held={held}
              full={full}
              wide={body.w >= 360}
              busy={busy}
              layout="modal"
              whenLine={whenLine}
              onKeep={onKeep}
              onThrow={onThrow}
            />
          </div>
          {error && (
            <Pen color={BRICK} size={15} style={{ marginTop: 10, textAlign: 'center' }}>
              {error}
            </Pen>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewCardModal;
